/**
 * The pe3d preview frame in live mode: the release's live template is loaded
 * once per variant (and per loading screen / restart), then every edit is sent
 * to the running game over the engine's bridge (modules/pe3dLive.js) instead
 * of reloading it — the game keeps its state, an asset swap shows in place.
 *
 * Returns `{ url, isLive, error, paused, setPaused, muted, setMuted, canMute }`.
 * `isLive` is false for a release without a live template; the caller then
 * previews by reloading, as before.
 *
 * Pause and mute are the editor's (bridge `pause` / `resume`, `set-muted` —
 * the latter from bridge protocol 3, so `canMute` says whether this release's
 * game takes it). Both survive a restart of the page: they are sent again once
 * the game is ready. While paused the game draws no frames, so every edit is
 * followed by a `render`: the change shows in the still frame.
 *
 * Requests go out only after the game says `playengine3d:ready`; edits made
 * while it boots are folded into one update when it does.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { BRIDGE_PREFIX, isBridgeMessage, planLiveUpdate } from "../modules/pe3dLive";

export default function usePe3dLiveFrame({ release, doc, draft, loadingScreen, reloadKey, iframeRef }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  const [epoch, setEpoch] = useState(0); // bumped when an edit cannot be sent and the frame must restart
  const [paused, setPausedState] = useState(false);
  const [muted, setMutedState] = useState(false);
  const [protocol, setProtocol] = useState(0);
  const pausedRef = useRef(false);
  const mutedRef = useRef(false);
  const appliedRef = useRef(null);
  const readyRef = useRef(false);
  const draftRef = useRef(draft);
  const busyRef = useRef(Promise.resolve());
  const requestSeq = useRef(0);
  draftRef.current = draft;

  const isLive = Boolean(release && release.hasLive);
  const docId = doc ? String(doc._id) : null;

  // The starting page: rebuilt for another variant, release, loading screen, restart or an unsendable edit.
  useEffect(() => {
    let cancelled = false;
    readyRef.current = false;
    if (!isLive || !draftRef.current) {
      setUrl(null);
      return undefined;
    }
    const start = draftRef.current;
    release
      .liveHtml(start, loadingScreen)
      .then((html) => {
        if (cancelled) return;
        appliedRef.current = start;
        setError(null);
        setUrl(URL.createObjectURL(new Blob([html], { type: "text/html" })));
      })
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [isLive, release, docId, loadingScreen, reloadKey, epoch]);

  // Each page is released once the next one replaces it (and on unmount).
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  const post = useCallback((name, payload) => {
    const target = iframeRef.current && iframeRef.current.contentWindow;
    if (!target) return;
    requestSeq.current += 1;
    target.postMessage({ type: `${BRIDGE_PREFIX}${name}`, requestId: `pe3d-${requestSeq.current}`, ...payload }, "*");
  }, [iframeRef]);

  // Send what changed since the game last heard from us. Serialised, so a slow
  // asset read never lets a later edit overtake an earlier one.
  const sync = useCallback(() => {
    busyRef.current = busyRef.current.then(async () => {
      if (!readyRef.current || !release || !draftRef.current) return;
      const plan = planLiveUpdate(appliedRef.current, draftRef.current, release);
      if (plan.rebuild) {
        setEpoch((value) => value + 1);
        return;
      }
      if (plan.config) post("set-config", { patch: plan.config });
      if (plan.playable) post("set-playable", plan.playable);
      for (const { slot, ref } of plan.slots) {
        try {
          post("set-asset", { slot, files: await release.liveFiles(slot, ref) });
        } catch (err) {
          // The file's problem is on its slot card already; the game keeps the old file.
          plan.applied.assets = { ...(plan.applied.assets || {}) };
          if (appliedRef.current && appliedRef.current.assets && appliedRef.current.assets[slot]) plan.applied.assets[slot] = appliedRef.current.assets[slot];
          else delete plan.applied.assets[slot];
        }
      }
      appliedRef.current = plan.applied;
      // A paused game draws nothing by itself: one frame, so the edit shows.
      if (pausedRef.current && (plan.config || plan.playable || plan.slots.length)) post("render", {});
    });
  }, [release, post]);

  const setPaused = useCallback((value) => {
    pausedRef.current = Boolean(value);
    setPausedState(pausedRef.current);
    if (readyRef.current) post(pausedRef.current ? "pause" : "resume", {});
  }, [post]);
  const setMuted = useCallback((value) => {
    mutedRef.current = Boolean(value);
    setMutedState(mutedRef.current);
    if (readyRef.current) post("set-muted", { muted: mutedRef.current });
  }, [post]);

  // The game announces itself once it has booted; a reply with an error means
  // the bridge refused a request, which a fresh start resolves.
  useEffect(() => {
    if (!isLive) return undefined;
    const onMessage = (event) => {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow || !isBridgeMessage(event.data)) return;
      if (event.data.type === `${BRIDGE_PREFIX}ready`) {
        readyRef.current = true;
        setProtocol(Number(event.data.protocol) || 0);
        // A fresh page starts running and audible: put the editor's choices back.
        if (mutedRef.current && Number(event.data.protocol) >= 3) post("set-muted", { muted: true });
        if (pausedRef.current) post("pause", {});
        sync();
      } else if (event.data.type === `${BRIDGE_PREFIX}result` && event.data.error) {
        console.warn("[pe3d live] bridge refused a request:", event.data.error);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [isLive, sync, post, iframeRef]);

  useEffect(() => {
    if (isLive) sync();
  }, [draft, isLive, sync]);

  return { url, isLive, error, paused, setPaused, muted, setMuted, canMute: protocol >= 3 };
}
